// Incident status vocabularies and presentation. Mirrors the CHECK constraints
// in src/migrate/sql/0017_incidents.sql — the backend rejects anything else.
//
// Every tone here is paired with a letter or shape elsewhere in the UI rather
// than standing alone. The packet prints on a mono laser and some readers
// cannot distinguish red from green, so colour is always reinforcement, never
// the only channel carrying the status.

import type { Accounted, ContactResult, SiteAccountability, SiteKind, Structure, Triage } from './api';

/** Axis 1 — the sweep. The headline number is computed from this alone. */
export const ACCOUNTED: { value: Accounted; label: string; short: string; tone: string }[] = [
  { value: 'unknown',   label: 'Unknown',   short: '?', tone: 'bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200' },
  { value: 'accounted', label: 'Accounted', short: 'A', tone: 'bg-emerald-600 text-white dark:bg-emerald-500' },
  { value: 'not_home',  label: 'Not home',  short: 'N', tone: 'bg-sky-600 text-white dark:bg-sky-500' },
  { value: 'missing',   label: 'Missing',   short: 'M', tone: 'bg-rose-600 text-white dark:bg-rose-500' },
];

/** Axis 2 — START triage. 'black' is the sole record of a death. */
export const TRIAGE: { value: Triage; label: string; short: string; tone: string }[] = [
  { value: 'green',  label: 'Minor',    short: 'G', tone: 'bg-emerald-600 text-white dark:bg-emerald-500' },
  { value: 'yellow', label: 'Delayed',  short: 'Y', tone: 'bg-amber-500 text-white' },
  { value: 'red',    label: 'Immediate', short: 'R', tone: 'bg-rose-600 text-white dark:bg-rose-500' },
  { value: 'black',  label: 'Deceased', short: 'B', tone: 'bg-slate-900 text-white dark:bg-slate-950 dark:ring-1 dark:ring-slate-600' },
];

/** Axis 3 — unmet need. An array, so it feeds a resource-request list. */
export const NEEDS = [
  'medical', 'water', 'food', 'shelter', 'power', 'transport', 'wellness_check',
] as const;

/** Pre-known accessibility / medical needs, carried in from `needs:` tags. */
export const SPECIAL_NEEDS = [
  'needs:oxygen', 'needs:mobility', 'needs:cognitive', 'needs:insulin',
  'needs:infant', 'needs:hearing', 'needs:vision', 'needs:language', 'needs:pet',
] as const;

/** What the operator observed at the door. Not a roll-up of the occupants. */
export const CONTACT_RESULTS: { value: ContactResult; label: string }[] = [
  { value: 'not_attempted', label: 'Not attempted' },
  { value: 'answered',      label: 'Answered' },
  { value: 'no_answer',     label: 'No answer' },
  { value: 'vacant',        label: 'Vacant' },
  { value: 'inaccessible',  label: 'Inaccessible' },
];

/** ATC-20 rapid evaluation placards. */
export const STRUCTURE: { value: Structure; label: string; tone: string }[] = [
  { value: 'unknown', label: 'Not evaluated', tone: 'bg-slate-200 text-slate-600 dark:bg-slate-700 dark:text-slate-300' },
  { value: 'green',   label: 'Inspected',     tone: 'bg-emerald-600 text-white dark:bg-emerald-500' },
  { value: 'yellow',  label: 'Restricted',    tone: 'bg-amber-500 text-white' },
  { value: 'red',     label: 'Unsafe',        tone: 'bg-rose-600 text-white dark:bg-rose-500' },
];

export const HAZARDS = [
  'gas', 'fire', 'water', 'electrical', 'structural', 'road_blocked',
] as const;

const ACCOUNTED_BY_VALUE = Object.fromEntries(ACCOUNTED.map((a) => [a.value, a]));
const TRIAGE_BY_VALUE = Object.fromEntries(TRIAGE.map((t) => [t.value, t]));
const STRUCTURE_BY_VALUE = Object.fromEntries(STRUCTURE.map((s) => [s.value, s]));

export const accountedMeta = (v: Accounted) => ACCOUNTED_BY_VALUE[v] ?? ACCOUNTED_BY_VALUE.unknown;
export const triageMeta = (v: Triage | null) => (v ? TRIAGE_BY_VALUE[v] : null);
export const structureMeta = (v: Structure) => STRUCTURE_BY_VALUE[v] ?? STRUCTURE_BY_VALUE.unknown;

/** Site roll-up presentation. The value itself is derived server-side. */
export const SITE_ACCOUNTABILITY: Record<SiteAccountability, { label: string; tone: string }> = {
  all_accounted:  { label: 'All accounted', tone: 'text-emerald-700 dark:text-emerald-300' },
  partial:        { label: 'Partial',       tone: 'text-amber-700 dark:text-amber-300' },
  none_accounted: { label: 'None accounted', tone: 'text-rose-700 dark:text-rose-300' },
  missing:        { label: 'Missing person', tone: 'text-rose-700 dark:text-rose-300 font-semibold' },
  no_occupants:   { label: 'No occupants',  tone: 'text-slate-400 dark:text-slate-500' },
};

/** snake_case or needs:-prefixed → Title Case. */
export function formatTerm(t: string): string {
  return t
    .replace(/^needs:/, '')
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

/** LCR addresses arrive as HTML with <br> separators. The seed strips them
 *  server-side, but older rows and hand-entered values can still carry tags,
 *  and a tag leaking into the printed packet is worse than one on screen.
 *  Mirrors stripHtml() in the household and person detail pages. */
export function stripHtml(s: string | null | undefined): string {
  if (!s) return '';
  return s.replace(/<br\s*\/?>/gi, ', ').replace(/<[^>]+>/g, '').trim();
}

/** What a site is for. Residence is the default and the overwhelming majority;
 *  the rest are the places an incident is run from or people are sent to. */
export const SITE_KINDS: { value: SiteKind; label: string; glyph: string }[] = [
  { value: 'residence',    label: 'Residence',    glyph: '⌂' },
  { value: 'command_post', label: 'Command post', glyph: '★' },
  { value: 'shelter',      label: 'Shelter',      glyph: '⛨' },
  { value: 'staging',      label: 'Staging area', glyph: '▣' },
  { value: 'aid_station',  label: 'Aid station',  glyph: '✚' },
  { value: 'other',        label: 'Other',        glyph: '•' },
];

const KIND_BY_VALUE = Object.fromEntries(SITE_KINDS.map((k) => [k.value, k]));
export const siteKindMeta = (v: SiteKind) => KIND_BY_VALUE[v] ?? KIND_BY_VALUE.residence;
